import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BrowserMediaStreamTransport,
  LiveKitAudioTransport,
  TableAudioClient,
  type AudioConnectionGrant,
  type TableAudioState,
  type VoiceMode,
} from "@guandan/audio-client";
import type { AvatarConfig } from "@guandan/avatar-core";
import type { BotDifficulty } from "@guandan/bot-engine";
import {
  NATURAL_RANKS,
  automaticTributeCard,
  legalReturnTributeCards,
  legalTributeCards,
  type CardId,
  type GuandanRuleProfile,
  type NaturalRank,
} from "@guandan/game-core";
import type { RevealedHandsByRound, ViewerSnapshot } from "@guandan/protocol";
import {
  ActionButton,
  GlassPanel,
  PlayingCard,
  SegmentedControl,
  UtilityButton,
} from "@guandan/ui";
import {
  cardFromId,
  createLockedGroup,
  groupCardsByRank,
  lockedCardIds,
  normalizedCombinationFromKey,
  reconcileLockedGroups,
  recognizeSmartGroups,
  sortCards,
  sortPlayedCardIds,
  type LockedGroup,
  type SortMode,
} from "../game/cards";
import {
  HINT_STRATEGIES,
  chooseLegalHint,
  createTableAuthority,
  makeAction,
  type AuthorityEvent,
  type TableAuthority,
} from "../game/table-controller";
import {
  GameSoundscape,
  MUSIC_TRACKS,
  soundscapeSettings,
} from "../game/game-sound";
import {
  reduceTributeReveals,
  type TributeReveal,
} from "../game/tribute-display";
import {
  estimateServerClockOffset,
  remainingTurnSeconds,
} from "../game/turn-clock";
import {
  EMPTY_VISIBLE_SEAT_PLAYS,
  reduceVisibleSeatPlays,
  shouldAnnounceLowHand,
} from "../game/table-event-state";
import {
  buildTableAvatarPresentations,
  defaultAvatarForSeat,
  relativeTablePosition,
} from "../game/table-avatars";
import { PortraitAvatar } from "./PortraitAvatar";
import { keepScreenAwake } from "../lib/pwa";
import { getSessionToken } from "../lib/session";
import { UserMusicPlayer } from "../lib/user-music";
import type { AppSettings, MatchRecord } from "../lib/storage";
import cardBackAsset from "../../../../assets/cards/card-back-v1.webp";
import tableSceneAsset from "../../../../assets/scenes/modern-lounge-haikou-table-empty-v3.webp";

const initialAudioState: TableAudioState = {
  connected: false,
  voiceMode: "push-to-talk",
  pushToTalkActive: false,
  speakerEnabled: true,
  tableMuted: false,
  networkQuality: "unknown",
  speakingParticipantIds: [],
  permissionError: null,
};

const finishOrderLabels = ["上游", "二游", "三游", "下游"] as const;
const TABLE_NOTICE_DURATION_MS = 5_000;

function handStackStepPx(stackSize: number, compactHand: boolean): number {
  if (!compactHand) return 32;
  if (stackSize >= 7) return 12;
  if (stackSize === 6) return 14;
  if (stackSize === 5) return 16;
  if (stackSize === 4) return 18;
  return 20;
}

function finishOrderLabel(order: number | null): string | null {
  if (order === null || order < 1 || order > 4) return null;
  return finishOrderLabels[order - 1] ?? null;
}

const combinationNames: Readonly<Record<string, string>> = {
  single: "单张",
  pair: "对子",
  triple: "三张",
  "full-house": "三带二",
  straight: "顺子",
  "consecutive-pairs": "三连对",
  "steel-plate": "钢板",
  bomb: "炸弹",
  "straight-flush": "同花顺",
  "joker-bomb": "四王炸",
};

const networkQualityNames: Readonly<
  Record<TableAudioState["networkQuality"], string>
> = {
  excellent: "极佳",
  good: "良好",
  poor: "较差",
  lost: "中断",
  unknown: "待测",
};

function audioErrorMessage(cause: unknown): string {
  const detail = cause instanceof Error ? cause.message : "未知错误";
  if (/permission|notallowed|denied|麦克风/iu.test(detail))
    return "麦克风权限未开启，请在浏览器或系统设置中允许后重试";
  if (/audio grant|语音授权|401|403/iu.test(detail))
    return "牌桌语音授权失败，请重新进入房间后重试";
  if (/connect|network|websocket|signal/iu.test(detail))
    return "LiveKit 连接失败，请检查网络后重试";
  return `语音暂不可用：${detail.slice(0, 80)}`;
}

export interface TableCompletion {
  readonly snapshot: ViewerSnapshot;
  readonly events: readonly string[];
  readonly authorityEvents: readonly AuthorityEvent[];
  readonly revealedHandsByRound?: RevealedHandsByRound;
}

export function GameTable({
  roomId,
  roomCode,
  mode,
  botDifficulty,
  allowPause,
  ruleProfile,
  initialVoiceMode,
  selfNickname,
  selfAvatar,
  settings,
  cardBackUrl = cardBackAsset,
  onSettingsChange,
  onExit,
  onEnd,
  onFinish,
}: {
  roomId: string;
  roomCode?: string;
  mode: MatchRecord["mode"];
  botDifficulty: BotDifficulty;
  allowPause: boolean;
  ruleProfile?: Partial<GuandanRuleProfile>;
  initialVoiceMode: VoiceMode;
  selfNickname: string;
  selfAvatar: AvatarConfig;
  settings: AppSettings;
  cardBackUrl?: string;
  onSettingsChange: (settings: AppSettings) => void;
  onExit: () => void;
  onEnd: () => Promise<void>;
  onFinish: (completion: TableCompletion) => void;
}) {
  const authority = useMemo<TableAuthority>(
    () =>
      createTableAuthority(
        roomId,
        botDifficulty,
        selfNickname,
        selfAvatar,
        ruleProfile,
      ),
    [botDifficulty, roomId, ruleProfile, selfAvatar, selfNickname],
  );
  const [snapshot, setSnapshot] = useState<ViewerSnapshot | null>(null);
  const [clockNow, setClockNow] = useState(() => Date.now());
  const [selected, setSelected] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [lockedGroups, setLockedGroups] = useState<readonly LockedGroup[]>([]);
  const [sortMode, setSortMode] = useState<SortMode>("rank");
  const [manualOrder, setManualOrder] = useState<readonly string[]>([]);
  const [draggedCardId, setDraggedCardId] = useState<string | null>(null);
  const [hintStrategyIndex, setHintStrategyIndex] = useState(0);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [ending, setEnding] = useState(false);
  const [message, setMessage] = useState("正在连接权威牌桌…");
  const [soundReady, setSoundReady] = useState(false);
  const [tributeReveals, setTributeReveals] = useState<
    readonly TributeReveal[]
  >([]);
  const [antiTributeVisible, setAntiTributeVisible] = useState(false);
  const [visibleSeatPlays, setVisibleSeatPlays] = useState(
    EMPTY_VISIBLE_SEAT_PLAYS,
  );
  const [voiceMode, setVoiceMode] = useState<VoiceMode>(initialVoiceMode);
  const [audioState, setAudioState] =
    useState<TableAudioState>(initialAudioState);
  const audioRef = useRef<TableAudioClient | null>(null);
  const audioConnectPromiseRef = useRef<Promise<TableAudioClient> | null>(null);
  const pushToTalkPressedRef = useRef(false);
  const gameSoundRef = useRef<GameSoundscape | null>(null);
  const userMusicRef = useRef<UserMusicPlayer | null>(null);
  const latestSnapshotRef = useRef<ViewerSnapshot | null>(null);
  if (gameSoundRef.current === null)
    gameSoundRef.current = new GameSoundscape(soundscapeSettings(settings));
  if (userMusicRef.current === null)
    userMusicRef.current = new UserMusicPlayer();
  const unsubscribeAudioRef = useRef<(() => void) | null>(null);
  const lastPlayedTurnRef = useRef<number | null>(null);
  const previousCurrentSeatRef = useRef<number | null>(null);
  const previousPhaseRef = useRef<string | null>(null);
  const previousRemainingCardsRef = useRef<ReadonlyMap<number, number>>(
    new Map(),
  );
  const lowHandAnnouncedSeatsRef = useRef<ReadonlySet<number>>(new Set());
  const lastAnnouncedEventSeq = useRef(-1);
  const automaticTributeAttemptRef = useRef<string | null>(null);
  const reorderTimer = useRef<number | null>(null);
  const pressedCard = useRef<{
    readonly id: string;
  } | null>(null);
  const dragActive = useRef(false);
  const reorderedDuringDrag = useRef(false);
  const suppressClick = useRef<string | null>(null);
  const eventsRef = useRef<readonly string[]>([`${mode}已开始`]);
  const completionStarted = useRef(false);
  const serverTimeOffset = useRef(0);
  const authorityLifecycle = useRef(0);
  const authorityCloseTimer = useRef<number | null>(null);
  const onFinishRef = useRef(onFinish);
  const selfAvatarRef = useRef(selfAvatar);
  onFinishRef.current = onFinish;
  selfAvatarRef.current = selfAvatar;
  const exitLabel = "暂离";

  useEffect(() => {
    const soundscape = gameSoundRef.current;
    if (!soundscape) return;
    const unlock = () => {
      void soundscape
        .unlock()
        .then(() => {
          setSoundReady(true);
          window.removeEventListener("pointerdown", unlock);
          window.removeEventListener("keydown", unlock);
        })
        .catch(() => setSoundReady(false));
    };
    window.addEventListener("pointerdown", unlock, { passive: true });
    window.addEventListener("keydown", unlock);
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      void soundscape.dispose();
      gameSoundRef.current = null;
      userMusicRef.current?.dispose();
      userMusicRef.current = null;
    };
  }, []);

  useEffect(() => {
    gameSoundRef.current?.applySettings(soundscapeSettings(settings));
  }, [
    settings.music,
    settings.musicTrack,
    settings.musicVolume,
    settings.soundEffects,
    settings.soundEffectsVolume,
    settings.customMusicTrackId,
  ]);

  useEffect(() => {
    const player = userMusicRef.current;
    const trackId = settings.customMusicTrackId;
    if (!player || !trackId || !settings.music) {
      player?.pause();
      return;
    }
    const controller = new AbortController();
    void player
      .prepare(trackId, controller.signal)
      .then(() => {
        if (soundReady) return player.play(settings.musicVolume);
      })
      .catch((cause: unknown) => {
        if (!controller.signal.aborted)
          setMessage(
            cause instanceof Error ? cause.message : "云端音乐加载失败",
          );
      });
    return () => controller.abort();
  }, [
    settings.customMusicTrackId,
    settings.music,
    settings.musicVolume,
    soundReady,
  ]);

  useEffect(() => {
    userMusicRef.current?.setVolume(settings.musicVolume);
  }, [settings.musicVolume]);

  const finish = useCallback(
    async (finalSnapshot: ViewerSnapshot) => {
      if (completionStarted.current) return;
      completionStarted.current = true;
      const visibleEvents = [...eventsRef.current, "本局结算完成"];
      eventsRef.current = visibleEvents;
      let authorityEvents: readonly AuthorityEvent[] = [];
      let revealedHandsByRound: RevealedHandsByRound | undefined;
      try {
        const replay = await authority.replay();
        authorityEvents = replay.events;
        revealedHandsByRound = replay.revealedHandsByRound;
      } catch {
        setMessage("牌局已结算；权威回放稍后可在战绩中重新同步");
      }
      onFinishRef.current({
        snapshot: finalSnapshot,
        events: visibleEvents,
        authorityEvents,
        ...(revealedHandsByRound ? { revealedHandsByRound } : {}),
      });
    },
    [authority],
  );

  useEffect(() => {
    const lifecycle = ++authorityLifecycle.current;
    if (authorityCloseTimer.current !== null) {
      window.clearTimeout(authorityCloseTimer.current);
      authorityCloseTimer.current = null;
    }
    let active = true;
    let transportOperation = 0;
    const receive = (value: ViewerSnapshot) => {
      if (!active) return;
      latestSnapshotRef.current = value;
      const localNow = Date.now();
      serverTimeOffset.current = estimateServerClockOffset(
        value.serverNow,
        localNow,
      );
      setClockNow(localNow);
      setSnapshot(value);
      if (value.phase === "settled")
        window.setTimeout(() => {
          void finish(value);
        }, 0);
    };
    const unsubscribeTable = authority.subscribe(receive);
    const unsubscribeEvents = authority.subscribeEvents((event) => {
      if (!active || event.seq <= lastAnnouncedEventSeq.current) return;
      lastAnnouncedEventSeq.current = event.seq;
      setTributeReveals((current) => reduceTributeReveals(current, event));
      setVisibleSeatPlays((current) => reduceVisibleSeatPlays(current, event));
      if (event.type === "ACE_RESET") {
        const team = event.payload.team;
        const selfTeam = latestSnapshotRef.current?.players.find(
          (player) => player.seat === latestSnapshotRef.current?.selfSeat,
        )?.team;
        const label = team === selfTeam ? "我方" : "对方";
        const notice = `${label}连续三把未打过 A，等级退回 2`;
        setMessage(`${notice}，重新开始升级`);
      }
      if (event.type === "CARDS_DEALT") {
        setAntiTributeVisible(false);
        lowHandAnnouncedSeatsRef.current = new Set();
      }
      if (event.type !== "ANTI_TRIBUTE") return;
      setAntiTributeVisible(true);
      const snapshot = latestSnapshotRef.current;
      const firstSeat = event.payload.firstSeat;
      const actor =
        typeof firstSeat === "number"
          ? snapshot?.players.find((player) => player.seat === firstSeat)
          : undefined;
      const gender =
        actor?.avatar?.gender ??
        (actor?.seat === snapshot?.selfSeat
          ? selfAvatarRef.current.gender
          : actor
            ? defaultAvatarForSeat(actor.seat).gender
            : selfAvatarRef.current.gender);
      gameSoundRef.current?.announceAntiTribute(gender);
      setMessage("哈哈，抗贡！这把稳了！");
    });
    const initialOperation = ++transportOperation;
    void (authority.resume?.() ?? authority.connect())
      .then((value) => {
        if (active && transportOperation === initialOperation) {
          receive(value);
          setMessage(
            value.phase === "settled"
              ? "牌局完成"
              : "轮到你出牌；长按拖动可理牌，选中后可锁定",
          );
        }
      })
      .catch((cause: unknown) => {
        if (!active || transportOperation !== initialOperation) return;
        setMessage(cause instanceof Error ? cause.message : "牌桌连接失败");
      });
    let releaseWakeLock: () => void = () => undefined;
    let wakeLockEpoch = 0;
    const acquireWakeLock = () => {
      const epoch = ++wakeLockEpoch;
      void keepScreenAwake().then((release) => {
        if (!active || epoch !== wakeLockEpoch) {
          release();
          return;
        }
        releaseWakeLock();
        releaseWakeLock = release;
      });
    };
    const suspendTransport = () => {
      if (!active) return;
      transportOperation += 1;
      pushToTalkPressedRef.current = false;
      if (audioRef.current) void audioRef.current.endPushToTalk();
      authority.suspend?.();
      wakeLockEpoch += 1;
      releaseWakeLock();
      releaseWakeLock = () => undefined;
      setMessage("已暂离牌桌；联网房将由服务器安全托管");
    };
    const resumeTransport = () => {
      if (!active || document.visibilityState === "hidden") return;
      acquireWakeLock();
      const operationId = ++transportOperation;
      const operation = authority.resume?.() ?? authority.connect();
      void operation
        .then((value) => {
          if (!active || transportOperation !== operationId) return;
          receive(value);
          setMessage("已返回牌桌，牌桌状态已恢复");
        })
        .catch((cause: unknown) => {
          if (!active || transportOperation !== operationId) return;
          setMessage(
            cause instanceof Error ? cause.message : "返回牌桌时恢复失败",
          );
        });
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") suspendTransport();
      else resumeTransport();
    };
    acquireWakeLock();
    const pointerUp = () => {
      pushToTalkPressedRef.current = false;
      if (audioRef.current) void audioRef.current.endPushToTalk();
      if (reorderTimer.current) window.clearTimeout(reorderTimer.current);
      if (reorderedDuringDrag.current && pressedCard.current)
        suppressClick.current = pressedCard.current.id;
      dragActive.current = false;
      reorderedDuringDrag.current = false;
      pressedCard.current = null;
      setDraggedCardId(null);
    };
    const installedPwa =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.matchMedia("(display-mode: fullscreen)").matches ||
      Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
    const mobileGameViewport =
      window.matchMedia("(pointer: coarse)").matches &&
      window.matchMedia("(max-width: 1100px)").matches;
    let fullscreenRequestPending = false;
    const enterImmersiveFullscreen = () => {
      if (
        (!installedPwa && !mobileGameViewport) ||
        document.fullscreenElement ||
        fullscreenRequestPending ||
        typeof document.documentElement.requestFullscreen !== "function"
      )
        return;
      fullscreenRequestPending = true;
      void document.documentElement
        .requestFullscreen({ navigationUI: "hide" })
        .catch(() => undefined)
        .finally(() => {
          fullscreenRequestPending = false;
        });
    };
    window.addEventListener("pointerup", pointerUp);
    if (installedPwa || mobileGameViewport)
      window.addEventListener("pointerdown", enterImmersiveFullscreen);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", suspendTransport);
    window.addEventListener("pageshow", resumeTransport);
    return () => {
      active = false;
      transportOperation += 1;
      wakeLockEpoch += 1;
      unsubscribeTable();
      unsubscribeEvents();
      authority.suspend?.();
      authorityCloseTimer.current = window.setTimeout(() => {
        if (authorityLifecycle.current === lifecycle) authority.close();
        authorityCloseTimer.current = null;
      }, 1_000);
      releaseWakeLock();
      window.removeEventListener("pointerup", pointerUp);
      window.removeEventListener("pointerdown", enterImmersiveFullscreen);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", suspendTransport);
      window.removeEventListener("pageshow", resumeTransport);
      unsubscribeAudioRef.current?.();
      unsubscribeAudioRef.current = null;
      if (audioRef.current) void audioRef.current.dispose();
      audioRef.current = null;
    };
  }, [authority, finish]);

  useEffect(() => {
    if (tributeReveals.length === 0) return;
    const timer = window.setTimeout(
      () => setTributeReveals([]),
      TABLE_NOTICE_DURATION_MS,
    );
    return () => window.clearTimeout(timer);
  }, [tributeReveals]);

  useEffect(() => {
    if (!antiTributeVisible) return;
    const timer = window.setTimeout(
      () => setAntiTributeVisible(false),
      TABLE_NOTICE_DURATION_MS,
    );
    return () => window.clearTimeout(timer);
  }, [antiTributeVisible]);

  useEffect(() => {
    if (
      !snapshot?.deadlineAt ||
      snapshot.paused ||
      snapshot.phase === "settled"
    )
      return;
    const tick = () => setClockNow(Date.now());
    tick();
    const timer = window.setInterval(tick, 250);
    return () => window.clearInterval(timer);
  }, [snapshot?.deadlineAt, snapshot?.paused, snapshot?.phase]);

  useEffect(() => {
    if (!snapshot) return;
    const muted = snapshot.tableMuted;
    if (audioRef.current) void audioRef.current.applyTableMuted(muted);
    else
      setAudioState((current) =>
        current.tableMuted === muted
          ? current
          : { ...current, tableMuted: muted },
      );
  }, [snapshot?.tableMuted]);

  useEffect(() => {
    const lastPlay = snapshot?.lastPlay;
    if (!lastPlay || lastPlayedTurnRef.current === lastPlay.turnIndex) return;
    lastPlayedTurnRef.current = lastPlay.turnIndex;
    const combination = normalizedCombinationFromKey(
      lastPlay.cardIds,
      lastPlay.combination,
    );
    gameSoundRef.current?.play(combination?.isBomb ? "bomb" : "play");
    if (!combination) return;
    const actor = snapshot.players.find(
      (player) => player.seat === lastPlay.seat,
    );
    const gender =
      actor?.avatar?.gender ??
      (actor?.seat === snapshot.selfSeat
        ? selfAvatar.gender
        : actor
          ? defaultAvatarForSeat(actor.seat).gender
          : "man");
    gameSoundRef.current?.announceCombination(combination.kind, gender);
  }, [selfAvatar, snapshot]);

  useEffect(() => {
    if (!snapshot) return;
    const previous = previousRemainingCardsRef.current;
    const next = new Map<number, number>();
    for (const player of snapshot.players) {
      const previousCount = previous.get(player.seat);
      next.set(player.seat, player.remainingCards);
      if (
        !shouldAnnounceLowHand(
          previousCount,
          player.remainingCards,
          lowHandAnnouncedSeatsRef.current.has(player.seat),
        )
      )
        continue;
      lowHandAnnouncedSeatsRef.current = new Set([
        ...lowHandAnnouncedSeatsRef.current,
        player.seat,
      ]);
      const gender =
        player.avatar?.gender ??
        (player.seat === snapshot.selfSeat
          ? selfAvatar.gender
          : defaultAvatarForSeat(player.seat).gender);
      gameSoundRef.current?.announceLowHand(gender);
      setMessage(`${player.nickname}剩余 ${player.remainingCards} 张牌`);
    }
    previousRemainingCardsRef.current = next;
  }, [selfAvatar, snapshot]);

  useEffect(() => {
    if (!snapshot) return;
    if (
      previousCurrentSeatRef.current !== null &&
      previousCurrentSeatRef.current !== snapshot.currentSeat &&
      snapshot.currentSeat === snapshot.selfSeat &&
      snapshot.phase === "playing"
    )
      gameSoundRef.current?.play("turn");
    previousCurrentSeatRef.current = snapshot.currentSeat;
  }, [snapshot?.currentSeat, snapshot?.phase, snapshot?.selfSeat]);

  useEffect(() => {
    if (
      snapshot?.phase === "settled" &&
      previousPhaseRef.current !== null &&
      previousPhaseRef.current !== "settled"
    )
      gameSoundRef.current?.play("settle");
    if (snapshot) previousPhaseRef.current = snapshot.phase;
  }, [snapshot?.phase]);

  const cards = useMemo(() => {
    if (!snapshot) return [];
    return sortCards(
      snapshot.selfHand.map((id, index) => cardFromId(id, index)),
      sortMode,
      {
        levelRank: snapshot.levelRank,
        lockedGroups,
        manualOrder,
        rankDirection: settings.handOrderDirection,
      },
    );
  }, [
    lockedGroups,
    manualOrder,
    settings.handOrderDirection,
    snapshot,
    sortMode,
  ]);
  const handColumns = useMemo(
    () =>
      sortMode === "original"
        ? cards.map((card) => ({ rank: card.rank, cards: [card] }))
        : groupCardsByRank(cards),
    [cards, sortMode],
  );
  const compactMobileHand =
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 1100px)").matches;
  const locked = useMemo(() => lockedCardIds(lockedGroups), [lockedGroups]);
  const recognizableGroups = useMemo(() => {
    if (!snapshot) return [];
    return recognizeSmartGroups(
      cards.filter((card) => !locked.has(card.id)),
      snapshot.levelRank,
    ).filter((group) => group.cardIds.length > 1);
  }, [cards, locked, snapshot]);
  const currentHintStrategy =
    HINT_STRATEGIES[hintStrategyIndex % HINT_STRATEGIES.length] ??
    HINT_STRATEGIES[0];
  const sortOptions = [
    { value: "rank", label: "按点数" },
    { value: "suit", label: "按花色" },
    { value: "type", label: "同花顺优先" },
    { value: "smart", label: "炸弹优先" },
    { value: "manual", label: "手动理牌" },
    { value: "original", label: "恢复" },
  ] as const;

  const endCurrentGame = useCallback(async () => {
    if (ending) return;
    setEnding(true);
    try {
      await onEnd();
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "结束牌局失败");
      setEnding(false);
    }
  }, [ending, onEnd]);

  const send = useCallback(
    async (action: Parameters<typeof makeAction>[1], label: string) => {
      if (!snapshot) return;
      try {
        const next = await authority.send(makeAction(snapshot, action));
        const nextEvents = [...eventsRef.current, label];
        eventsRef.current = nextEvents;
        setSnapshot(next);
        setSelected(new Set());
        const communicationConfirmation =
          action.type === "QUICK_PHRASE" || action.type === "EMOTE"
            ? `${label} · 已全桌发送`
            : "权威状态已同步";
        setMessage(
          next.phase === "settled" ? "牌局完成" : communicationConfirmation,
        );
        if (action.type === "PASS") gameSoundRef.current?.play("pass");
        if (next.phase === "settled") void finish(next);
      } catch (cause) {
        setMessage(cause instanceof Error ? cause.message : "操作未完成");
      }
    },
    [authority, finish, snapshot],
  );

  useEffect(() => {
    if (
      !snapshot ||
      snapshot.phase !== "tribute" ||
      snapshot.tributeAction?.kind !== "offer" ||
      snapshot.tributeAction.actorSeat !== snapshot.selfSeat
    )
      return;
    const level = (
      (NATURAL_RANKS as readonly string[]).includes(snapshot.levelRank)
        ? snapshot.levelRank
        : "2"
    ) as NaturalRank;
    const cardId = automaticTributeCard(
      snapshot.selfHand as readonly CardId[],
      level,
    );
    if (!cardId) return;
    const attemptKey = `${snapshot.version}:${cardId}`;
    if (automaticTributeAttemptRef.current === attemptKey) return;
    automaticTributeAttemptRef.current = attemptKey;
    setMessage("检测到大小王，正在按规则自动进贡");
    void send({ type: "OFFER_TRIBUTE", cardId }, "系统已自动完成进贡");
  }, [send, snapshot]);

  useEffect(() => {
    if (!snapshot) return;
    setLockedGroups((current) => {
      const reconciled = reconcileLockedGroups(current, snapshot.selfHand);
      return reconciled.length === current.length &&
        reconciled.every((group, index) => group.id === current[index]?.id)
        ? current
        : reconciled;
    });
    setManualOrder((current) => {
      const inHand = new Set(snapshot.selfHand);
      const kept = current.filter((cardId) => inHand.has(cardId));
      const keptIds = new Set(kept);
      const next = [
        ...kept,
        ...snapshot.selfHand.filter((cardId) => !keptIds.has(cardId)),
      ];
      return next.length === current.length &&
        next.every((cardId, index) => cardId === current[index])
        ? current
        : next;
    });
  }, [snapshot]);

  function toggleCard(id: string) {
    if (suppressClick.current === id) {
      suppressClick.current = null;
      return;
    }
    const currentSnapshot = snapshot;
    const pendingTribute =
      currentSnapshot?.phase === "tribute" &&
      currentSnapshot.tributeAction?.actorSeat === currentSnapshot.selfSeat
        ? currentSnapshot.tributeAction
        : null;
    if (pendingTribute && currentSnapshot) {
      const level = (
        (NATURAL_RANKS as readonly string[]).includes(currentSnapshot.levelRank)
          ? currentSnapshot.levelRank
          : "2"
      ) as NaturalRank;
      const legalCards =
        pendingTribute.kind === "offer"
          ? legalTributeCards(
              currentSnapshot.selfHand as readonly CardId[],
              level,
            )
          : legalReturnTributeCards(
              currentSnapshot.selfHand as readonly CardId[],
              level,
            );
      if (!legalCards.includes(id as CardId)) {
        setMessage(
          pendingTribute.kind === "offer"
            ? "这张牌不符合进贡规则，请选择手中点力最大的非红桃级牌"
            : "这张牌不能还贡，请选择十点及以下且非级牌；若没有，才可选择手中最小牌",
        );
        return;
      }
      setSelected((current) => (current.has(id) ? new Set() : new Set([id])));
      setMessage(
        pendingTribute.kind === "offer"
          ? "已选择进贡牌，确认无误后点击“进贡”"
          : "已选择还贡牌，你可以改选其他合法牌；确认后点击“还贡”",
      );
      return;
    }
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function activateCardDrag(id: string) {
    if (locked.has(id)) return;
    dragActive.current = true;
    reorderedDuringDrag.current = false;
    setDraggedCardId(id);
    setSortMode("manual");
    setManualOrder(cards.map((card) => card.id));
    if (settings.vibration) navigator.vibrate(24);
  }

  function startCardPress(id: string) {
    pressedCard.current = { id };
    if (reorderTimer.current) window.clearTimeout(reorderTimer.current);
    reorderTimer.current = window.setTimeout(() => {
      reorderTimer.current = null;
      activateCardDrag(id);
    }, 280);
  }

  function moveCard(event: React.PointerEvent<HTMLDivElement>) {
    const pressed = pressedCard.current;
    if (!pressed || locked.has(pressed.id)) return;
    if (!dragActive.current) return;
    event.preventDefault();
    const target = document
      .elementFromPoint(event.clientX, event.clientY)
      ?.closest<HTMLElement>("[data-card-id]");
    const targetId = target?.dataset.cardId;
    if (!targetId || targetId === pressed.id || locked.has(targetId)) return;
    setManualOrder((current) => {
      const base =
        current.length > 0 ? [...current] : cards.map((card) => card.id);
      const from = base.indexOf(pressed.id);
      const targetIndex = base.indexOf(targetId);
      if (from < 0 || targetIndex < 0) return current;
      base.splice(from, 1);
      const insertAt = base.indexOf(targetId);
      base.splice(insertAt, 0, pressed.id);
      reorderedDuringDrag.current = true;
      return base;
    });
  }

  function finishCardDrag() {
    if (reorderTimer.current) window.clearTimeout(reorderTimer.current);
    if (reorderedDuringDrag.current && pressedCard.current)
      suppressClick.current = pressedCard.current.id;
    dragActive.current = false;
    reorderedDuringDrag.current = false;
    pressedCard.current = null;
    setDraggedCardId(null);
  }

  function toggleLockedGroupForCard(id: string) {
    const existing = lockedGroups.find((group) => group.cardIds.includes(id));
    if (existing) {
      setLockedGroups((current) =>
        current.filter((group) => group.id !== existing.id),
      );
      setMessage(`已解锁${existing.label}`);
      return;
    }
    const selectedCardIds =
      selected.has(id) && selected.size > 1
        ? cards
            .filter((card) => selected.has(card.id) && !locked.has(card.id))
            .map((card) => card.id)
        : [];
    const recognized = recognizableGroups.find((group) =>
      group.cardIds.includes(id),
    );
    lockAsGroup(
      selectedCardIds.length > 1
        ? selectedCardIds
        : (recognized?.cardIds ?? [id]),
    );
  }

  function lockSelectedCards() {
    const cardIds = cards
      .filter((card) => selected.has(card.id) && !locked.has(card.id))
      .map((card) => card.id);
    if (cardIds.length === 0) {
      setMessage("请先选择尚未锁定的牌张");
      return;
    }
    lockAsGroup(cardIds);
  }

  function lockAsGroup(cardIds: readonly string[]) {
    if (!snapshot) return;
    const group = createLockedGroup(cardIds, snapshot.levelRank);
    setLockedGroups((current) => {
      const occupied = lockedCardIds(current);
      if (group.cardIds.some((cardId) => occupied.has(cardId))) return current;
      return [...current, group];
    });
    setSelected((current) => {
      const next = new Set(current);
      for (const cardId of group.cardIds) next.delete(cardId);
      return next;
    });
    setMessage(group.explanation);
  }

  async function ensureAudio(): Promise<TableAudioClient> {
    if (audioRef.current) return audioRef.current;
    if (audioConnectPromiseRef.current)
      return await audioConnectPromiseRef.current;
    const connecting = connectAudio();
    audioConnectPromiseRef.current = connecting;
    try {
      return await connecting;
    } finally {
      audioConnectPromiseRef.current = null;
    }
  }

  async function connectAudio(): Promise<TableAudioClient> {
    const api = import.meta.env.VITE_GAME_API_URL as string | undefined;
    const token = getSessionToken();
    const isServerRoom = /^[0-9a-f]{8}-[0-9a-f-]{27}$/iu.test(roomId);
    let client: TableAudioClient;
    let grant: AudioConnectionGrant;
    if (api && token && isServerRoom) {
      const response = await fetch(`${api.replace(/\/$/u, "")}/audio/token`, {
        method: "POST",
        headers: {
          authorization: `Bearer ${token}`,
          "content-type": "application/json",
        },
        body: JSON.stringify({ roomId }),
      });
      if (!response.ok) throw new Error("无法取得本牌桌语音授权");
      grant = (await response.json()) as AudioConnectionGrant;
      const { Room } = await import("livekit-client");
      client = new TableAudioClient({
        transport: new LiveKitAudioTransport(
          new Room({ adaptiveStream: true, dynacast: true }),
        ),
      });
    } else {
      grant = {
        serverUrl: "local://media-stream",
        roomName: roomId,
        participantId: "local-player",
        token: "local-development-audio",
        expiresAt: Date.now() + 60 * 60_000,
      };
      client = new TableAudioClient({
        transport: new BrowserMediaStreamTransport(),
      });
    }
    const unsubscribeAudio = client.subscribe(setAudioState);
    unsubscribeAudioRef.current = unsubscribeAudio;
    try {
      await client.connect(grant);
      await client.applyTableMuted(snapshot?.tableMuted ?? false);
      if (!audioState.speakerEnabled) await client.setSpeakerEnabled(false);
      audioRef.current = client;
      return client;
    } catch (cause) {
      unsubscribeAudio();
      unsubscribeAudioRef.current = null;
      await client.dispose();
      throw cause;
    }
  }

  async function startVoice() {
    if (voiceMode === "muted") return;
    pushToTalkPressedRef.current = true;
    try {
      if (!audioRef.current) {
        const probe = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: false,
        });
        for (const track of probe.getTracks()) track.stop();
      }
      const client = await ensureAudio();
      await client.setVoiceMode("push-to-talk");
      if (!isPushToTalkStillPressed()) return;
      await client.beginPushToTalk();
      setMessage("正在向全桌说话，最长 15 秒");
    } catch (cause) {
      setMessage(audioErrorMessage(cause));
    }
  }

  function isPushToTalkStillPressed(): boolean {
    return pushToTalkPressedRef.current;
  }

  function endVoice() {
    pushToTalkPressedRef.current = false;
    if (voiceMode !== "open-mic" && audioRef.current)
      void audioRef.current.endPushToTalk();
  }
  async function changeVoice(modeValue: VoiceMode) {
    setVoiceMode(modeValue);
    try {
      const client = await ensureAudio();
      await client.setVoiceMode(modeValue);
    } catch (cause) {
      setMessage(audioErrorMessage(cause));
    }
  }

  useEffect(() => {
    if (
      snapshot?.phase !== "tribute" ||
      snapshot.tributeAction?.actorSeat !== snapshot.selfSeat
    )
      return;
    setSelected(new Set());
    setMessage(
      snapshot.tributeAction.kind === "return"
        ? "请你自己选择一张合法牌还贡；系统不会代选或自动还牌"
        : "请选择要进贡的合法牌；只有大小王会按规则自动进贡",
    );
  }, [
    snapshot?.phase,
    snapshot?.selfSeat,
    snapshot?.tributeAction?.actorSeat,
    snapshot?.tributeAction?.kind,
    snapshot?.tributeAction?.toSeat,
  ]);

  if (!snapshot)
    return (
      <main className="game-loading">
        <GlassPanel>
          <div className="loading-orbit" aria-hidden="true" />
          <h1>正在进入牌桌</h1>
          <p>{message}</p>
          <ActionButton tone="ghost" onClick={onExit}>
            {exitLabel}
          </ActionButton>
        </GlassPanel>
      </main>
    );

  const canAct =
    snapshot.currentSeat === snapshot.selfSeat &&
    (snapshot.phase === "playing" || snapshot.phase === "tribute") &&
    !snapshot.paused;
  const voiceOpen =
    audioState.pushToTalkActive ||
    (audioState.connected &&
      audioState.voiceMode === "open-mic" &&
      !audioState.tableMuted);
  const tribute =
    snapshot.phase === "tribute" ? (snapshot.tributeAction ?? null) : null;
  const currentLevel = (
    (NATURAL_RANKS as readonly string[]).includes(snapshot.levelRank)
      ? snapshot.levelRank
      : "2"
  ) as NaturalRank;
  const tributeLegalCardIds = new Set<string>(
    tribute?.actorSeat === snapshot.selfSeat
      ? tribute.kind === "offer"
        ? legalTributeCards(
            snapshot.selfHand as readonly CardId[],
            currentLevel,
          )
        : legalReturnTributeCards(
            snapshot.selfHand as readonly CardId[],
            currentLevel,
          )
      : [],
  );

  const turnSecondsRemaining = remainingTurnSeconds(
    snapshot.deadlineAt,
    clockNow,
    serverTimeOffset.current,
  );
  const selfPlayer = snapshot.players.find(
    (player) => player.seat === snapshot.selfSeat,
  );
  const trusteeEnabled = Boolean(selfPlayer?.isBot && selfPlayer.avatar);
  const seatPlays = visibleSeatPlays.plays;
  const speakingParticipantIds =
    voiceOpen &&
    selfPlayer &&
    !audioState.speakingParticipantIds.includes(selfPlayer.userId)
      ? [...audioState.speakingParticipantIds, selfPlayer.userId]
      : audioState.speakingParticipantIds;
  const avatarPresentations = buildTableAvatarPresentations({
    players: snapshot.players,
    selfSeat: snapshot.selfSeat,
    currentSeat: snapshot.currentSeat,
    selfAvatar,
    speakingParticipantIds,
  });
  const isOwner = selfPlayer?.userId === snapshot.ownerUserId;
  const canPause = mode === "电脑练习" || allowPause;
  const networkSummary = navigator.onLine
    ? audioState.connected
      ? `网络在线，语音质量${networkQualityNames[audioState.networkQuality]}`
      : "网络在线，语音尚未连接"
    : "网络已离线，等待恢复权威连接";
  const playerName = (seat: number | null) =>
    seat === null
      ? "等待收贡方"
      : (snapshot.players.find((player) => player.seat === seat)?.nickname ??
        `第${seat + 1}席`);
  const unlockSound = () => {
    void Promise.all([
      gameSoundRef.current?.unlock(),
      settings.music && settings.customMusicTrackId
        ? userMusicRef.current?.play(settings.musicVolume)
        : Promise.resolve(),
    ])
      .then(() => {
        setSoundReady(true);
        setMessage(
          settings.music
            ? `背景音乐已播放：${settings.customMusicTrackName ?? MUSIC_TRACKS.find((track) => track.id === settings.musicTrack)?.label ?? "当前曲目"}`
            : "牌桌音效已开启；可在声音工具中打开背景音乐",
        );
      })
      .catch(() => setMessage("浏览器暂未允许声音，请再次轻触开启"));
  };
  return (
    <main
      className="game-table"
      style={
        {
          "--card-back-image": cardBackUrl ? `url(${cardBackUrl})` : undefined,
          "--table-scene-image": `url(${tableSceneAsset})`,
        } as React.CSSProperties
      }
    >
      <header className="table-topbar">
        <div className="table-round-info">
          <strong>{mode}</strong>
          <b className="team-level-indicator">
            我方打{" "}
            {snapshot.teamLevels?.[selfPlayer?.team ?? 0] ?? snapshot.levelRank}
            <span>
              对方打{" "}
              {snapshot.teamLevels?.[(selfPlayer?.team ?? 0) === 0 ? 1 : 0] ??
                snapshot.levelRank}
            </span>
          </b>
          <b className="level-rank-indicator">本局级牌 {snapshot.levelRank}</b>
          <div
            className="turn-clock"
            aria-label={
              snapshot.paused
                ? "牌局已暂停"
                : turnSecondsRemaining === null
                  ? "等待下一回合"
                  : `回合剩余 ${turnSecondsRemaining} 秒`
            }
          >
            {snapshot.paused
              ? "停"
              : snapshot.phase === "settled"
                ? "终"
                : (turnSecondsRemaining ?? "…")}
          </div>
          <span>
            房号 {(roomCode ?? roomId.replace(/\D/g, "").slice(-6)) || "本地"}
          </span>
        </div>
        <div className="table-utilities">
          <UtilityButton
            className="table-utility--network"
            onClick={() => setMessage(networkSummary)}
          >
            {navigator.onLine
              ? `网络${audioState.connected ? networkQualityNames[audioState.networkQuality] : "在线"}`
              : "网络离线"}
          </UtilityButton>
          <UtilityButton
            className="table-utility--tools"
            aria-expanded={toolsOpen}
            onClick={() => setToolsOpen((open) => !open)}
          >
            桌面工具
          </UtilityButton>
          {canPause ? (
            <UtilityButton
              className="table-utility--pause"
              onClick={() =>
                void send(
                  { type: snapshot.paused ? "RESUME" : "PAUSE" },
                  snapshot.paused ? "牌局已恢复" : "牌局已暂停",
                )
              }
            >
              {snapshot.paused ? "恢复" : "暂停"}
            </UtilityButton>
          ) : null}
          <UtilityButton className="table-utility--exit" onClick={onExit}>
            {exitLabel}
          </UtilityButton>
          <UtilityButton
            className="table-utility--end"
            disabled={ending}
            onClick={() => void endCurrentGame()}
          >
            {ending ? "结束中" : "结束"}
          </UtilityButton>
        </div>
      </header>
      <section className="table-players" aria-label="牌桌四席现代角色">
        {avatarPresentations.map((presentation) => {
          const { player } = presentation;
          const self = player.seat === snapshot.selfSeat;
          const finishedAs = finishOrderLabel(player.finishOrder);
          return (
            <article
              className={`player-badge player-badge--position-${presentation.relativePosition}${presentation.speaking ? " player-badge--speaking" : ""}${self ? " player-badge--self" : ""}`}
              key={player.userId}
              aria-label={`${presentation.accessibleLabel}${finishedAs ? `，${finishedAs}，已出完` : player.remainingCards <= 10 ? `，剩余 ${player.remainingCards} 张牌` : ""}`}
              aria-current={
                snapshot.currentSeat === player.seat ? "true" : undefined
              }
              data-avatar-source={presentation.source}
              data-low-hand={
                finishedAs || player.remainingCards <= 10 ? "true" : "false"
              }
              data-finish-order={player.finishOrder ?? undefined}
            >
              <PortraitAvatar
                config={presentation.config}
                motion={presentation.motion}
                className="table-seat-avatar"
                label={presentation.accessibleLabel}
              />
              <div className="player-badge__details">
                <b>{player.nickname}</b>
                <small>
                  {finishedAs
                    ? `${finishedAs} · 已出完`
                    : `${self ? "你" : player.isBot ? "AI" : "玩家"}${player.remainingCards <= 10 ? ` · 剩余 ${player.remainingCards} 张` : ""}`}
                </small>
                <small className="player-avatar-source">
                  {presentation.sourceLabel}
                </small>
              </div>
              {presentation.speaking ? <em role="status">说话中 )))</em> : null}
              {snapshot.currentSeat === player.seat ? <i>出牌中</i> : null}
            </article>
          );
        })}
      </section>
      <section className="jade-table" aria-label="玉石绿牌桌">
        <div className="table-watermark" aria-hidden="true">
          掼蛋
        </div>
        {antiTributeVisible ? (
          <div className="tribute-banner tribute-banner--anti" role="status">
            <strong>抗贡成立</strong>
            <span>进贡方两人合计持有两张大王，本局免进贡、免还贡</span>
          </div>
        ) : tributeReveals.length > 0 ? (
          <div className="tribute-banner" aria-label="本局进贡与还贡明牌">
            {tributeReveals.map((reveal) => {
              const card = cardFromId(reveal.cardId);
              return (
                <article key={reveal.key}>
                  <span>
                    <strong>
                      {reveal.tributeKind === "double"
                        ? "双贡"
                        : reveal.tributeKind === "inner"
                          ? "内贡"
                          : "单贡"}
                      ·{reveal.kind === "offer" ? "给牌" : "还牌"}
                    </strong>
                    {playerName(reveal.fromSeat)} → {playerName(reveal.toSeat)}
                  </span>
                  <PlayingCard
                    rank={card.rank}
                    suit={card.suit}
                    red={card.red}
                    tabIndex={-1}
                  />
                </article>
              );
            })}
          </div>
        ) : null}
        {seatPlays.size > 0 ? (
          <div className="seat-plays" aria-label="本墩各家已出的牌">
            {[...seatPlays.values()].map((play) => {
              const relative = relativeTablePosition(
                play.seat,
                snapshot.selfSeat,
              );
              return (
                <article
                  className={`seat-play seat-play--position-${relative}`}
                  key={play.seat}
                >
                  <p>
                    {playerName(play.seat)} ·{" "}
                    {combinationLabel(play.combination.kind)}
                  </p>
                  <div>
                    {sortPlayedCardIds(play.cardIds, play.combination).map(
                      (id) => {
                        const card = cardFromId(id);
                        return (
                          <PlayingCard
                            key={id}
                            rank={card.rank}
                            suit={card.suit}
                            red={card.red}
                            tabIndex={-1}
                          />
                        );
                      },
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        ) : null}
      </section>
      <aside className="sort-rail">
        <details className="sort-rail-panel">
          <summary>理牌</summary>
          <div className="sort-rail-content">
            <SegmentedControl
              label="理牌方式"
              options={sortOptions}
              value={sortMode}
              onChange={setSortMode}
            />
            <label className="mobile-sort-select">
              <span>方式</span>
              <select
                aria-label="理牌"
                value={sortMode}
                onChange={(event) =>
                  setSortMode(event.target.value as SortMode)
                }
              >
                {sortOptions.map((option) => (
                  <option value={option.value} key={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="rank-direction-select">
              <span>点数方向</span>
              <select
                aria-label="点数从小到大的排列方向"
                value={settings.handOrderDirection}
                onChange={(event) =>
                  onSettingsChange({
                    ...settings,
                    handOrderDirection: event.target
                      .value as AppSettings["handOrderDirection"],
                  })
                }
              >
                <option value="ascending-left-to-right">小→大（左到右）</option>
                <option value="ascending-right-to-left">大←小（右到左）</option>
              </select>
            </label>
          </div>
        </details>
      </aside>
      <section
        className="hand-area"
        style={
          {
            "--playing-card-width":
              settings.cardSize === "small"
                ? "48px"
                : settings.cardSize === "large"
                  ? "72px"
                  : "60px",
            "--hand-card-count": cards.length,
          } as React.CSSProperties
        }
        aria-label="你的手牌"
      >
        <details className="hand-assist-panel">
          <summary>
            提示策略 · {currentHintStrategy?.label ?? "最小压制"}
          </summary>
          <div className="hand-assist">
            <div className="hint-strategy" role="status" aria-live="polite">
              <span>
                提示策略：<strong>{currentHintStrategy?.label}</strong>
              </span>
              <small>{currentHintStrategy?.description}</small>
              <button
                type="button"
                onClick={() =>
                  setHintStrategyIndex(
                    (current) => (current + 1) % HINT_STRATEGIES.length,
                  )
                }
                aria-label="切换到下一种提示策略"
              >
                换策略
              </button>
            </div>
            <div className="lock-group-strip" aria-label="已锁定组合">
              <button
                type="button"
                onClick={lockSelectedCards}
                disabled={selected.size === 0}
              >
                锁定所选
              </button>
              {lockedGroups.length === 0 ? (
                <span>暂无锁组</span>
              ) : (
                lockedGroups.map((group) => (
                  <button
                    type="button"
                    key={group.id}
                    data-kind={group.kind}
                    title={`${group.explanation} 点击解锁。`}
                    onClick={() => {
                      setLockedGroups((current) =>
                        current.filter(
                          (candidate) => candidate.id !== group.id,
                        ),
                      );
                      setMessage(`已解锁${group.label}`);
                    }}
                    aria-label={`解锁${group.label}，${group.cardIds.length}张`}
                  >
                    {group.label} · {group.cardIds.length}张 ×
                  </button>
                ))
              )}
            </div>
          </div>
        </details>
        <button
          type="button"
          className="hand-lock-action"
          onClick={lockSelectedCards}
          disabled={selected.size === 0}
        >
          {selected.size > 0 ? `锁定所选（${selected.size}）` : "选牌后锁定"}
        </button>
        <div
          className={`hand-cards${sortMode === "original" ? " hand-cards--single-row" : ""}${draggedCardId ? " hand-cards--dragging" : ""}`}
          onPointerMove={moveCard}
          onPointerUp={finishCardDrag}
          onPointerCancel={finishCardDrag}
        >
          {handColumns.map((column) => {
            const stackStep = handStackStepPx(
              column.cards.length,
              compactMobileHand,
            );
            return (
              <div
                className="hand-card-column"
                data-rank={column.rank}
                data-stack-size={column.cards.length}
                key={
                  sortMode === "original" ? column.cards[0]?.id : column.rank
                }
                style={
                  {
                    "--hand-stack-extra": `${Math.max(0, column.cards.length - 1) * stackStep}px`,
                  } as React.CSSProperties
                }
              >
                {column.cards.map((card, stackIndex) => (
                  <PlayingCard
                    key={card.id}
                    data-card-id={card.id}
                    className={
                      draggedCardId === card.id
                        ? "playing-card--dragging"
                        : undefined
                    }
                    style={
                      {
                        "--hand-stack-index": stackIndex,
                        "--hand-stack-top": `${stackIndex * stackStep}px`,
                      } as React.CSSProperties
                    }
                    rank={card.rank}
                    suit={card.suit}
                    red={card.red}
                    selected={selected.has(card.id)}
                    locked={locked.has(card.id)}
                    onPointerDown={() => startCardPress(card.id)}
                    onKeyDown={(event) => {
                      if (event.key.toLowerCase() === "l") {
                        event.preventDefault();
                        toggleLockedGroupForCard(card.id);
                      }
                    }}
                    onClick={() => toggleCard(card.id)}
                    aria-keyshortcuts="L"
                    aria-description="同点数牌纵向成列；长按拖动可理牌；按 L 锁定或解锁组合"
                    aria-label={`${card.rank}${card.suit}${locked.has(card.id) ? "，已锁定组合" : ""}`}
                  />
                ))}
              </div>
            );
          })}
        </div>
        <p className="table-message" role="status">
          {message}
        </p>
        <div className="play-controls">
          <ActionButton
            tone="blue"
            disabled={
              !canAct || (tribute?.kind === "return" && selected.size === 0)
            }
            onClick={() => {
              if (tribute?.kind === "return") {
                setSelected(new Set());
                setMessage("已清除选择，请重新点选你要还贡的合法牌");
                return;
              }
              if (!currentHintStrategy) return;
              const hint = chooseLegalHint(
                snapshot,
                tribute ? new Set<string>() : locked,
                currentHintStrategy.id,
              );
              setSelected(new Set(hint?.cardIds ?? []));
              setMessage(
                hint
                  ? `${hint.strategyLabel}：已选择${combinationLabel(hint.combinationKind)}，${hint.description}`
                  : "当前没有可压过桌面的牌",
              );
            }}
          >
            {tribute?.kind === "return"
              ? "重选还贡牌"
              : tribute
                ? "按规则选牌"
                : "按策略提示"}
          </ActionButton>
          {tribute ? (
            <ActionButton
              tone="orange"
              disabled={
                !canAct ||
                selected.size !== 1 ||
                !tributeLegalCardIds.has([...selected][0] ?? "")
              }
              onClick={() => {
                const cardId = [...selected][0];
                if (!cardId) return;
                void send(
                  tribute.kind === "offer"
                    ? { type: "OFFER_TRIBUTE", cardId }
                    : {
                        type: "RETURN_TRIBUTE",
                        to: tribute.toSeat ?? 0,
                        cardId,
                      },
                  tribute.kind === "offer" ? "你完成进贡" : "你完成还贡",
                );
              }}
            >
              {tribute.kind === "offer" ? "进贡" : "还贡"}
            </ActionButton>
          ) : (
            <>
              <ActionButton
                tone="jade"
                disabled={!canAct || !snapshot.lastPlay}
                onClick={() => void send({ type: "PASS" }, "你选择不出")}
              >
                不出
              </ActionButton>
              <ActionButton
                tone="orange"
                disabled={!canAct || selected.size === 0}
                onClick={() =>
                  void send(
                    { type: "PLAY_CARDS", cardIds: [...selected] },
                    `你打出 ${selected.size} 张牌`,
                  )
                }
              >
                出牌
              </ActionButton>
            </>
          )}
        </div>
      </section>
      <aside
        className={`table-side-controls${toolsOpen ? " table-side-controls--open" : ""}`}
        aria-label="桌面工具面板"
      >
        <div className="table-tools-content">
          <div className="mobile-game-actions" aria-label="牌局操作">
            {canPause ? (
              <UtilityButton
                onClick={() =>
                  void send(
                    { type: snapshot.paused ? "RESUME" : "PAUSE" },
                    snapshot.paused ? "牌局已恢复" : "牌局已暂停",
                  )
                }
              >
                {snapshot.paused ? "恢复" : "暂停"}
              </UtilityButton>
            ) : null}
            <UtilityButton onClick={onExit}>{exitLabel}</UtilityButton>
            <UtilityButton
              disabled={ending}
              onClick={() => void endCurrentGame()}
            >
              {ending ? "结束中" : "结束牌局"}
            </UtilityButton>
          </div>
          <UtilityButton
            className="table-utility--trustee"
            aria-pressed={trusteeEnabled}
            onClick={() =>
              void send(
                { type: "SET_TRUSTEE", enabled: !trusteeEnabled },
                trusteeEnabled ? "已取消电脑托管" : "已开启电脑托管",
              )
            }
          >
            {trusteeEnabled ? "取消电脑托管" : "开启电脑托管"}
          </UtilityButton>
          <section className="table-sound-controls" aria-label="牌桌声音">
            <strong>音乐与音效</strong>
            <button
              type="button"
              className="table-sound-test"
              onClick={unlockSound}
            >
              {soundReady ? "音乐播放中 · 点击试听" : "开启并试听音乐"}
            </button>
            <label>
              <input
                type="checkbox"
                checked={settings.music}
                onChange={(event) =>
                  onSettingsChange({
                    ...settings,
                    music: event.target.checked,
                  })
                }
              />
              背景音乐
            </label>
            <select
              aria-label="牌桌背景曲目"
              value={
                settings.customMusicTrackId ? "custom" : settings.musicTrack
              }
              disabled={!settings.music}
              onChange={(event) =>
                event.target.value !== "custom" &&
                onSettingsChange({
                  ...settings,
                  musicTrack: event.target.value as AppSettings["musicTrack"],
                  customMusicTrackId: null,
                  customMusicTrackName: null,
                })
              }
            >
              {settings.customMusicTrackId ? (
                <option value="custom">
                  我的音乐 · {settings.customMusicTrackName ?? "已上传曲目"}
                </option>
              ) : null}
              {MUSIC_TRACKS.map((track) => (
                <option value={track.id} key={track.id}>
                  {track.label}
                </option>
              ))}
            </select>
            <label className="table-volume-row">
              音乐音量
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={settings.musicVolume}
                disabled={!settings.music}
                onChange={(event) =>
                  onSettingsChange({
                    ...settings,
                    musicVolume: Number(event.target.value),
                  })
                }
              />
            </label>
            <label>
              <input
                type="checkbox"
                checked={settings.soundEffects}
                onChange={(event) =>
                  onSettingsChange({
                    ...settings,
                    soundEffects: event.target.checked,
                  })
                }
              />
              出牌音效
            </label>
            <label className="table-volume-row">
              音效音量
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={settings.soundEffectsVolume}
                disabled={!settings.soundEffects}
                onChange={(event) =>
                  onSettingsChange({
                    ...settings,
                    soundEffectsVolume: Number(event.target.value),
                  })
                }
              />
            </label>
          </section>
          {initialVoiceMode === "muted" ? (
            <span role="status">本房间未启用语音</span>
          ) : (
            <>
              <ActionButton
                tone={voiceOpen ? "orange" : "ghost"}
                size="sm"
                onPointerDown={(event) => {
                  event.currentTarget.setPointerCapture(event.pointerId);
                  if (voiceMode === "push-to-talk") void startVoice();
                }}
                onPointerUp={endVoice}
                onPointerCancel={endVoice}
                onKeyDown={(event) => {
                  if (
                    (event.key === " " || event.key === "Enter") &&
                    !event.repeat &&
                    voiceMode === "push-to-talk"
                  ) {
                    event.preventDefault();
                    void startVoice();
                  }
                }}
                onKeyUp={(event) => {
                  if (event.key === " " || event.key === "Enter") {
                    event.preventDefault();
                    endVoice();
                  }
                }}
                onBlur={endVoice}
              >
                {voiceOpen ? "正在全桌说话" : "按住说话"}
              </ActionButton>
              <UtilityButton
                onClick={() =>
                  void changeVoice(
                    voiceMode === "open-mic" ? "push-to-talk" : "open-mic",
                  )
                }
              >
                {voiceMode === "open-mic" ? "常开麦已选" : "常开麦"}
              </UtilityButton>
              <UtilityButton
                onClick={() =>
                  void changeVoice(
                    voiceMode === "muted" ? "push-to-talk" : "muted",
                  )
                }
              >
                {voiceMode === "muted" ? "取消静音" : "静音"}
              </UtilityButton>
              <UtilityButton
                onClick={() => {
                  const enabled = !audioState.speakerEnabled;
                  if (audioRef.current)
                    void audioRef.current.setSpeakerEnabled(enabled);
                  else
                    setAudioState((value) => ({
                      ...value,
                      speakerEnabled: enabled,
                    }));
                }}
              >
                {audioState.speakerEnabled ? "关闭扬声器" : "开启扬声器"}
              </UtilityButton>
              {isOwner ? (
                <UtilityButton
                  onClick={() =>
                    void send(
                      {
                        type: "HOST_MUTE_TABLE",
                        muted: !snapshot.tableMuted,
                      },
                      snapshot.tableMuted
                        ? "房主已解除全桌静音"
                        : "房主已静音全桌",
                    )
                  }
                >
                  {snapshot.tableMuted ? "解除全桌静音" : "静音全桌"}
                </UtilityButton>
              ) : snapshot.tableMuted ? (
                <span role="status">房主已静音全桌</span>
              ) : null}
            </>
          )}
        </div>
      </aside>
    </main>
  );
}

function combinationLabel(value: string): string {
  const key = value.split("|", 1)[0] ?? value;
  return combinationNames[key] ?? value;
}
